export interface Cosmetic {
  id: string;
  name: string;
  type: 'avatar_color' | 'celebration' | 'badge' | 'title';
  value: string;
  unlockLevel: number;
  description: string;
}

export const COSMETICS: Cosmetic[] = [
  // Avatar Colors (unlocked progressively)
  { id: 'color_green', name: 'Chaos Green', type: 'avatar_color', value: 'hsl(160 84% 39%)', unlockLevel: 1, description: 'The classic green' },
  { id: 'color_blue', name: 'Ocean Blue', type: 'avatar_color', value: 'hsl(210 100% 50%)', unlockLevel: 2, description: 'Cool and calm' },
  { id: 'color_purple', name: 'Royal Purple', type: 'avatar_color', value: 'hsl(270 70% 50%)', unlockLevel: 3, description: 'Fit for royalty' },
  { id: 'color_orange', name: 'Sunset Orange', type: 'avatar_color', value: 'hsl(30 100% 50%)', unlockLevel: 5, description: 'Warm vibes' },
  { id: 'color_pink', name: 'Hot Pink', type: 'avatar_color', value: 'hsl(330 100% 60%)', unlockLevel: 7, description: 'Bold and beautiful' },
  { id: 'color_gold', name: 'Golden', type: 'avatar_color', value: 'hsl(45 100% 50%)', unlockLevel: 10, description: 'Precious!' },
  { id: 'color_rainbow', name: 'Rainbow', type: 'avatar_color', value: 'linear-gradient(135deg, red, orange, yellow, green, blue, purple)', unlockLevel: 15, description: 'All the colors!' },
  
  // Celebrations (particle effects)
  { id: 'celebration_confetti', name: 'Confetti', type: 'celebration', value: 'confetti', unlockLevel: 1, description: 'Classic celebration' },
  { id: 'celebration_fireworks', name: 'Fireworks', type: 'celebration', value: 'fireworks', unlockLevel: 4, description: 'Explosive joy' },
  { id: 'celebration_stars', name: 'Star Shower', type: 'celebration', value: 'stars', unlockLevel: 8, description: 'Reach for the stars' },
  { id: 'celebration_emoji', name: 'Emoji Rain', type: 'celebration', value: 'emoji', unlockLevel: 12, description: '🎉 Rain down! 🎊' },
  
  // Badges
  { id: 'badge_newbie', name: 'Newbie', type: 'badge', value: '🌱', unlockLevel: 1, description: 'Just getting started' },
  { id: 'badge_player', name: 'Player', type: 'badge', value: '🎮', unlockLevel: 3, description: 'Getting the hang of it' },
  { id: 'badge_veteran', name: 'Veteran', type: 'badge', value: '⭐', unlockLevel: 6, description: 'Experienced player' },
  { id: 'badge_master', name: 'Master', type: 'badge', value: '👑', unlockLevel: 10, description: 'Mastered the chaos' },
  { id: 'badge_legend', name: 'Legend', type: 'badge', value: '🏆', unlockLevel: 15, description: 'Legendary status' },
  { id: 'badge_chaos', name: 'Chaos Lord', type: 'badge', value: '💀', unlockLevel: 20, description: 'Lord of Chaos' },
  
  // Titles
  { id: 'title_player', name: 'Player', type: 'title', value: 'Player', unlockLevel: 1, description: 'Default title' },
  { id: 'title_rookie', name: 'Rookie', type: 'title', value: 'Rookie', unlockLevel: 2, description: 'Just starting out' },
  { id: 'title_chaos_apprentice', name: 'Chaos Apprentice', type: 'title', value: 'Chaos Apprentice', unlockLevel: 5, description: 'Learning the ways' },
  { id: 'title_number_cruncher', name: 'Number Cruncher', type: 'title', value: 'Number Cruncher', unlockLevel: 8, description: 'Math is fun!' },
  { id: 'title_chaos_master', name: 'Chaos Master', type: 'title', value: 'Chaos Master', unlockLevel: 12, description: 'Master of 67' },
  { id: 'title_legend', name: 'Living Legend', type: 'title', value: 'Living Legend', unlockLevel: 18, description: 'Legendary!' },
  { id: 'title_chaos_god', name: 'Chaos God', type: 'title', value: 'Chaos God', unlockLevel: 25, description: 'Ultimate power' },
];

export interface PlayerCosmetics {
  avatarColor: string;
  celebration: string;
  badge: string;
  title: string;
}

const SELECTED_KEY = '67chaos-cosmetics';

export function getDefaultCosmetics(): PlayerCosmetics {
  return {
    avatarColor: 'color_green',
    celebration: 'celebration_confetti',
    badge: 'badge_newbie',
    title: 'title_player',
  };
}

export function loadPlayerCosmetics(): PlayerCosmetics {
  const stored = localStorage.getItem(SELECTED_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return getDefaultCosmetics();
    }
  }
  return getDefaultCosmetics();
}

export function savePlayerCosmetics(cosmetics: PlayerCosmetics): void {
  localStorage.setItem(SELECTED_KEY, JSON.stringify(cosmetics));
}

export function getCosmeticById(id: string): Cosmetic | undefined {
  return COSMETICS.find(c => c.id === id);
}

export function getUnlockedCosmetics(level: number): Cosmetic[] {
  return COSMETICS.filter(c => c.unlockLevel <= level);
}

export function getCosmeticsByType(type: Cosmetic['type']): Cosmetic[] {
  return COSMETICS.filter(c => c.type === type);
}

export function isUnlocked(cosmeticId: string, level: number): boolean {
  const cosmetic = getCosmeticById(cosmeticId);
  return cosmetic ? cosmetic.unlockLevel <= level : false;
}